import React from "react";
import DoWeRobot from "@/components/DoWeRobot";
import { useT } from "@/lib/i18n";
import { Sparkles, Users2, Calendar, Compass, Star, Crown } from "lucide-react";

/**
 * École de Management — Exemple anonymisé de parcours + présentation de DoWe.
 */

const ICONS = [Star, Compass, Compass, Users2, Crown];
const COLORS = ["#FFFFFF", "#9DBC1D", "#E62C86", "#F3A114", "#EEE316"];

export default function DoWeJourney() {
  const t = useT();
  const milestones = t("doWeJourney.milestones", []) || [];
  const dowe = t("doWeJourney.dowe", {}) || {};

  return (
    <>
      {/* === Journey === */}
      <section data-testid="ecole-journey" className="bg-brand-deep text-white py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="/brand/gradient_wedo.jpg" alt="" className="w-full h-full object-cover mix-blend-overlay" />
        </div>

        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-3 mb-6 px-4 py-2 border border-accent-pink/40 rounded-full bg-accent-pink/10">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-pink animate-pulse" />
                <span className="text-[11px] uppercase tracking-[0.3em] text-accent-pink font-semibold">
                  {t("doWeJourney.badge")}
                </span>
              </div>
              <p className="section-num text-white/85">{t("doWeJourney.eyebrow")}</p>
              <h2 className="display text-3xl md:text-5xl lg:text-6xl leading-[1.05] mt-6">
                {t("doWeJourney.titleA")}<br/>
                <span className="text-accent-yellow">{t("doWeJourney.titleAccent")}</span>
              </h2>
            </div>
            <div className="md:col-span-5 md:pt-6">
              <p className="text-white/85 text-base md:text-lg leading-relaxed">
                {t("doWeJourney.lede")}
              </p>
            </div>
          </div>

          <div className="relative mt-16">
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-white/15 md:-translate-x-px" />
            <div className="space-y-2">
              {milestones.map((s, i) => {
                const Icon = ICONS[i] || Compass;
                const color = COLORS[i] || "#9DBC1D";
                const onLeft = i % 2 === 0;
                return (
                  <div
                    key={i}
                    data-testid={`journey-step-${i}`}
                    className="relative md:grid md:grid-cols-2 md:gap-12 md:py-6"
                  >
                    <div className="absolute left-6 md:left-1/2 -translate-x-1/2 top-2 md:top-10 z-10">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center border-2 border-brand-deep shadow-lg"
                        style={{ background: color }}
                      >
                        <Icon size={20} className="text-brand-deep" strokeWidth={2.4} />
                      </div>
                    </div>

                    <div className={`${onLeft ? "pl-20 md:pl-0 md:pr-16 md:text-right" : "hidden md:block"}`}>
                      {onLeft && (
                        <>
                          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] mb-2 text-white/85">
                            <Calendar size={11}/>
                            <span>{s.when}</span>
                          </div>
                          <h3 className="display text-xl md:text-2xl lg:text-3xl leading-tight" style={{ color }}>
                            {s.label}
                          </h3>
                          <p className="text-white/90 text-sm md:text-[15px] leading-relaxed mt-3 max-w-md md:ml-auto">
                            {s.desc}
                          </p>
                        </>
                      )}
                    </div>

                    <div className={`${!onLeft ? "pl-20 md:pl-16" : "hidden md:block"}`}>
                      {!onLeft && (
                        <>
                          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] mb-2 text-white/85">
                            <Calendar size={11}/>
                            <span>{s.when}</span>
                          </div>
                          <h3 className="display text-xl md:text-2xl lg:text-3xl leading-tight" style={{ color }}>
                            {s.label}
                          </h3>
                          <p className="text-white/90 text-sm md:text-[15px] leading-relaxed mt-3 max-w-md">
                            {s.desc}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-16 border-t border-white/15 pt-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <p className="text-[12px] uppercase tracking-[0.3em] text-white/90 display italic">
              {t("doWeJourney.footerNote")}
            </p>
            <p className="text-white/85 text-sm leading-relaxed">
              {t("doWeJourney.footerText")}
            </p>
          </div>
        </div>
      </section>

      {/* === DoWe === */}
      <section data-testid="dowe-companion" className="bg-paper py-24 md:py-32 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-accent-pink/10 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-accent-lime/10 blur-3xl" />

        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">

            <div className="md:col-span-5 flex flex-col items-start gap-8">
              <div className="inline-flex items-center gap-3 px-4 py-2 border border-brand/20 rounded-full bg-brand/5">
                <Sparkles size={13} className="text-accent-pink"/>
                <span className="text-[11px] uppercase tracking-[0.3em] text-brand font-semibold">
                  {dowe.badge}
                </span>
              </div>
              <div className="relative">
                <div className="absolute -inset-8 bg-gradient-to-br from-accent-pink/30 via-accent-orange/20 to-accent-lime/30 blur-3xl rounded-full" />
                <div className="relative">
                  <DoWeRobot size={220} />
                </div>
              </div>
              <div>
                <p className="section-num text-accent-pink mb-3">{dowe.eyebrow}</p>
                <h2 className="display text-5xl md:text-6xl lg:text-7xl leading-none">
                  Do<span className="text-accent-pink">.</span>We
                </h2>
                <p className="text-ink/75 text-base md:text-lg leading-relaxed mt-6 max-w-md">
                  {dowe.lede}
                </p>
              </div>
            </div>

            <div className="md:col-span-7">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-accent-pink via-accent-lime to-accent-yellow opacity-40 blur-2xl rounded-3xl" />
                <div className="relative bg-paper-2 rounded-2xl shadow-2xl overflow-hidden border border-ink/10">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-ink/10 bg-paper">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center overflow-hidden">
                        <DoWeRobot size={28} />
                      </div>
                      <div>
                        <div className="display italic text-sm text-brand">{dowe.chatTitle}</div>
                        <div className="flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-accent-lime animate-pulse"/>
                          <span className="text-[10px] text-ink/50 uppercase tracking-wider">{dowe.chatStatus}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-ink/15"/>
                      <span className="w-2 h-2 rounded-full bg-ink/15"/>
                      <span className="w-2 h-2 rounded-full bg-ink/15"/>
                    </div>
                  </div>

                  <div className="p-5 md:p-7 space-y-4 bg-gradient-to-b from-paper-2 to-paper">
                    <div className="flex justify-end">
                      <div className="bg-brand text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-[78%] text-[14px] leading-relaxed shadow-sm">
                        {dowe.userMsg}
                      </div>
                    </div>

                    <div className="flex justify-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-brand shrink-0 flex items-center justify-center overflow-hidden mt-1">
                        <DoWeRobot size={32} />
                      </div>
                      <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 max-w-[80%] text-[14px] leading-relaxed shadow-sm border border-ink/5">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles size={13} className="text-accent-pink"/>
                          <span className="display italic text-xs text-brand">{dowe.botLabel}</span>
                        </div>
                        <p>{dowe.botMsg}</p>
                        <ul className="mt-2 space-y-1 text-ink/80">
                          {(dowe.botList || []).map((it, i) => <li key={i}>· {it}</li>)}
                        </ul>
                      </div>
                    </div>

                    <div className="flex justify-start gap-3">
                      <div className="w-9 h-9 shrink-0" />
                      <div className="bg-white/80 rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[80%] text-[13px] leading-relaxed text-ink/70 italic border border-ink/5">
                        {dowe.botNote}
                      </div>
                    </div>

                    <div className="border-t border-ink/10 pt-3 mt-2 flex items-center gap-3 text-[12px] text-ink/40">
                      <div className="flex-1 bg-white border border-ink/10 rounded-full px-4 py-2 text-ink/40">
                        {dowe.placeholder}
                      </div>
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-lime animate-pulse"/>
                      <span className="text-[10px] uppercase tracking-wider display italic text-brand">{dowe.confidential}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-8 md:absolute md:-bottom-6 md:-right-4 md:max-w-xs bg-brand-deep text-white p-5 rounded-xl shadow-xl md:rotate-[1.5deg]">
                  <p className="text-sm leading-relaxed">{dowe.quote}</p>
                  <p className="display italic text-xs text-accent-pink mt-2">{dowe.quoteAuthor}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
